import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { almacen, type LotExpiry } from '../api';
import { CuadroDeBusqueda } from '../Buscable';
import { filtrarFilas } from '../busqueda';
import {
  Boton,
  Cabecera,
  Cargando,
  Cifra,
  ConAyuda,
  Fallo,
  Filtro,
  Pastilla,
  Vacio,
} from '../componentes';
import { VENCIMIENTO, cantidad, dia, soles, solesRedondos } from '../formato';
import { mayuscula, useVocabulario } from '../vocabulario';

/**
 * Lotes y vencimientos.
 *
 * Ordenados por lo que vence antes, que es el mismo orden en el que salen: la
 * descarga es FEFO —primero vence, primero sale— y esta pantalla es esa cola,
 * vista de frente.
 */
/**
 * Con cuántos días de anticipación se avisa. Lo elige quien mira; el de
 * partida es el del negocio (`lotSettings`): cinco en la cocina, noventa en
 * la botica, que mira a tres y seis meses para el canje con el laboratorio.
 */
const MARGENES = [3, 5, 7, 15, 30, 90, 180] as const;

/** Lo que se quiere ver: todo, o solo lo que pide atención. */
const FILTROS: readonly { id: 'todos' | LotExpiry; texto: string }[] = [
  { id: 'todos', texto: 'Todos' },
  { id: 'expiring', texto: 'Por vencer' },
  { id: 'expired', texto: 'Vencidos' },
];

/**
 * La dirección que abre Mermas con este lote ya puesto: el insumo, el lote,
 * lo que queda y el motivo. Antes había que ir a Mermas, buscar el insumo,
 * elegir el lote, teclear la cantidad y el motivo: cuatro pasos para tirar lo
 * que esta misma pantalla ya sabía que había que tirar.
 */
export function bajaDelLote(lotId: string): string {
  return `#/mermas/baja/${lotId}`;
}

/** Los días de vida mínimos para vender que se ofrecen: cero es lo de siempre. */
const DIAS_MINIMOS_PARA_VENDER = [0, 7, 15, 30, 60, 90] as const;

export function Lotes() {
  const vocabulario = useVocabulario();
  const clientes = useQueryClient();
  const ajustes = useQuery({
    queryKey: ['ajustes-de-lotes'],
    queryFn: () => almacen.lotSettings(),
    // Un servidor de antes no tiene la ruta: se queda en los cinco de siempre.
    retry: false,
  });
  const delNegocio = ajustes.data?.expiryAlertDays ?? 5;
  const [elegido, setMargen] = useState<number | null>(null);
  const margen = elegido ?? delNegocio;

  /** Que el margen elegido sea el del negocio: el del correo diario y el de partida. */
  const guardarMargen = useMutation({
    mutationFn: () =>
      almacen.saveLotSettings({
        requiresLotExpiry: ajustes.data?.requiresLotExpiry ?? false,
        expiryAlertDays: margen,
      }),
    onSuccess: () => {
      setMargen(null);
      void clientes.invalidateQueries({ queryKey: ['ajustes-de-lotes'] });
      void clientes.invalidateQueries({ queryKey: ['lotes'] });
    },
  });
  /*
   * El correo del almacén al momento o con el barrido (docs/15 §2.36). La
   * tienda lo quiere al momento: lo que se acabó en el mostrador es una venta
   * perdida hoy. La cocina, con el barrido: lo lee a las seis camino del
   * mercado, y treinta correos en pleno servicio serían ruido.
   */
  const guardarAlMomento = useMutation({
    mutationFn: (alMomento: boolean) =>
      almacen.saveLotSettings({
        requiresLotExpiry: ajustes.data?.requiresLotExpiry ?? false,
        expiryAlertDays: delNegocio,
        immediateAlerts: alMomento,
      }),
    onSuccess: () => {
      void clientes.invalidateQueries({ queryKey: ['ajustes-de-lotes'] });
    },
  });
  /*
   * No vender a menos de N días de vencer (migración 273): la botica no le
   * vende al paciente un jarabe que vence antes de terminar el tratamiento.
   * Solo con lo vencido retenido en la venta: sin eso, la caja no mira lotes.
   */
  const guardarDiasMinimos = useMutation({
    mutationFn: (dias: number) =>
      almacen.saveLotSettings({
        requiresLotExpiry: ajustes.data?.requiresLotExpiry ?? false,
        expiryAlertDays: delNegocio,
        saleMinDaysToExpiry: dias,
      }),
    onSuccess: () => {
      void clientes.invalidateQueries({ queryKey: ['ajustes-de-lotes'] });
    },
  });
  const [filtro, setFiltro] = useState<'todos' | LotExpiry>('todos');
  const [texto, setTexto] = useState('');

  const consulta = useQuery({
    queryKey: ['lotes', elegido],
    // Sin elegir, el servidor usa el margen del negocio.
    queryFn: () => almacen.lots(undefined, elegido ?? undefined),
  });

  const lotes = consulta.data ?? [];
  const visibles = filtrarFilas(
    filtro === 'todos' ? lotes : lotes.filter((lote) => lote.expiry === filtro),
    texto,
    (lote) => [lote.supply, lote.code, lote.supplier],
  );
  const vencidos = lotes.filter((lote) => lote.expiry === 'expired');
  const porVencer = lotes.filter((lote) => lote.expiry === 'expiring');

  const enRiesgo = [...vencidos, ...porVencer].reduce(
    (suma, lote) => suma + Number.parseFloat(lote.value),
    0,
  );

  const ubicaciones = new Set(lotes.map((lote) => lote.supply)).size;

  return (
    <>
      <Cabecera
        titulo="Lotes y vencimientos"
        subtitulo="Trazabilidad por lote con descarga FEFO" ayuda="Cada entrega de lo que caduca es un lote con su vencimiento. El almacén saca primero lo que vence antes (eso es FEFO) y avisa antes de que algo se pase."
      />

      <div className="cuerpo">
        {consulta.isError ? (
          <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} />
        ) : null}

        <div className="cifras">
          <Cifra
            rotulo="Lotes activos"
            ayuda="Lotes con algo dentro."
            valor={String(lotes.length)}
            apoyo={`de ${ubicaciones} ${vocabulario.insumos}`}
          />
          <Cifra
            rotulo="Por vencer"
            ayuda="Lotes que vencen dentro del margen de aviso elegido abajo."
            valor={String(porVencer.length)}
            apoyo="usar primero (FEFO)"
            tono={porVencer.length > 0 ? 'aviso' : 'neutro'}
          />
          <Cifra
            rotulo="Vencidos"
            ayuda="Lotes vencidos que siguen en el almacén. Hay que darlos de baja."
            valor={String(vencidos.length)}
            apoyo={
              vencidos.length > 0
                ? 'dar de baja en Mermas'
                : 'ninguno esperando'
            }
            tono={vencidos.length > 0 ? 'peligro' : 'neutro'}
          />
          <Cifra
            rotulo="Valor en riesgo"
            ayuda="Lo que vale lo que está por vencer o vencido."
            valor={solesRedondos(enRiesgo)}
            apoyo="si no se consume a tiempo"
          />
        </div>

        <p className="aviso-caja">
          La descarga automática usa FEFO: primero vence, primero sale. No es lo
          mismo que por orden de llegada — el pescado que entró ayer con dos días
          de vida se usa antes que el de anteayer congelado.
        </p>

        {ajustes.data?.immediateAlerts === undefined ? null : (
          <>
            <button
              type="button"
              className={`opcion ${ajustes.data.immediateAlerts ? 'opcion--activa' : ''}`}
              onClick={() => guardarAlMomento.mutate(!ajustes.data!.immediateAlerts)}
              aria-pressed={ajustes.data.immediateAlerts}
              disabled={guardarAlMomento.isPending}
            >
              Avisar por correo en cuanto algo baja de su mínimo
              <Pastilla
                texto={ajustes.data.immediateAlerts ? 'al momento' : 'cada pocas horas'}
                tono={ajustes.data.immediateAlerts ? 'aviso' : 'neutro'}
              />
            </button>
            {guardarAlMomento.isError ? <Fallo error={guardarAlMomento.error} /> : null}
          </>
        )}

        {ajustes.data?.withholdsExpiredAtSale !== true ||
        ajustes.data.saleMinDaysToExpiry === undefined ? null : (
          <div className="filtros" role="group" aria-label="Días mínimos de vida para vender">
            <span className="versal">No vender con menos de</span>
            {DIAS_MINIMOS_PARA_VENDER.map((dias) => (
              <Filtro
                key={dias}
                texto={dias === 0 ? 'hasta que vence' : `${dias} días`}
                activo={ajustes.data!.saleMinDaysToExpiry === dias}
                onClick={() => guardarDiasMinimos.mutate(dias)}
              />
            ))}
            <span className="nota-sm">
              Lo que vence antes no se ofrece en la caja ni sale de su lote: se queda para su merma
              o la devolución al laboratorio.
            </span>
            {guardarDiasMinimos.isError ? <Fallo error={guardarDiasMinimos.error} /> : null}
          </div>
        )}

        {lotes.length > 0 ? (
          <div className="filtros">
            <CuadroDeBusqueda
              valor={texto}
              onCambio={setTexto}
              placeholder={`Buscar ${vocabulario.insumo}, lote o proveedor…`}
              etiquetaAccesible="Buscar lote"
            />
            <span className="separador" />
            {FILTROS.map((opcion) => (
              <Filtro
                key={opcion.id}
                texto={opcion.texto}
                cuenta={
                  opcion.id === 'todos'
                    ? lotes.length
                    : lotes.filter((lote) => lote.expiry === opcion.id).length
                }
                activo={filtro === opcion.id}
                onClick={() => setFiltro(opcion.id)}
              />
            ))}
            <span className="separador" />
            <span className="versal">Avisar con</span>
            {MARGENES.map((dias) => (
              <Filtro
                key={dias}
                texto={`${dias} días`}
                activo={margen === dias}
                onClick={() => setMargen(dias)}
              />
            ))}
            {ajustes.data !== undefined && margen !== delNegocio ? (
              <Boton onClick={() => guardarMargen.mutate()} disabled={guardarMargen.isPending}>
                Avisar siempre con {margen} días
              </Boton>
            ) : null}
          </div>
        ) : null}

        {consulta.isLoading ? (
          <Cargando que="los lotes" />
        ) : lotes.length === 0 ? (
          <Vacio
            titulo="Ningún lote en el almacén"
            mensaje={`Los lotes nacen al recibir mercadería de un ${vocabulario.insumo} que se controla por lote. Recibe una entrada y aparecerá aquí con su vencimiento.`}
          />
        ) : (
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  <th>{mayuscula(vocabulario.insumo)}</th>
                  <th><ConAyuda ayuda="El código del lote: el del proveedor o el que puso el sistema al recibir.">Lote</ConAyuda></th>
                  <th className="numero">Cantidad</th>
                  <th className="numero">Valor</th>
                  <th><ConAyuda ayuda="El día en que entró al almacén.">Ingreso</ConAyuda></th>
                  <th><ConAyuda ayuda="La fecha de vencimiento. Sale primero lo que vence antes.">Vence</ConAyuda></th>
                  <th><ConAyuda ayuda="Vigente, por vencer (en los próximos días) o vencido (hay que darlo de baja).">Estado</ConAyuda></th>
                  <th aria-label="Acciones" />
                </tr>
              </thead>
              <tbody>
                {visibles.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="apagado">
                      Ningún lote con ese filtro.
                    </td>
                  </tr>
                ) : null}
                {visibles.map((lote) => {
                  const estado = VENCIMIENTO[lote.expiry];

                  return (
                    <tr key={lote.id}>
                      <td>
                        <div className="celda-nombre__texto">
                          <strong>{lote.supply}</strong>
                          {lote.supplier === null ? null : (
                            <span>{lote.supplier}</span>
                          )}
                        </div>
                      </td>
                      <td className="mono">{lote.code}</td>
                      <td className="numero">
                        {cantidad(lote.quantity)} {lote.unit}
                      </td>
                      <td className="numero">{soles(lote.value)}</td>
                      <td className="apagado mono">{dia(lote.receivedAt)}</td>
                      <td className={lote.expiry === 'expired' ? 'negativo' : ''}>
                        {lote.expiryLabel}
                      </td>
                      <td>
                        <Pastilla texto={estado.texto} tono={estado.tono} />
                      </td>
                      <td>
                        {lote.expiry === 'fresh' ? null : (
                          <a className="enlace-sutil" href={bajaDelLote(lote.id)}>
                            Dar de baja
                          </a>
                        )}{' '}
                        {/* Por dónde salió y en qué ventas: el kardex de este lote. */}
                        <a className="enlace-sutil" href={`#/kardex/lote/${encodeURIComponent(lote.code)}`}>
                          Rastrear
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
